import { useEffect, useState } from "react";
import { CalendarHeart } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const CATEGORIES = [
  "Lieu", "Traiteur", "Boissons / Vins", "Pâtissier / Pièce montée",
  "Photographe", "Vidéaste", "DJ / Animation", "Musiciens / Groupe",
  "Fleuriste", "Décoration", "Robe de mariée", "Costume", "Alliances / Bijoux",
  "Coiffure & Maquillage", "Faire-part / Papeterie", "Cadeaux invités",
  "Officiant", "Wedding Planner", "Transport", "Hébergement", "Autre"
];
const STATUSES = [
  { id: "to_contact", label: "À contacter" },
  { id: "contacted", label: "Contacté" },
  { id: "negotiating", label: "Négociation" },
  { id: "booked", label: "Réservé" },
  { id: "paid", label: "Payé" },
];

export default function Vendors() {
  const isCollaboratorPortal = window.location.pathname.startsWith("/collaborator");
  const [vendors, setVendors] = useState([]);
  const emptyVendor = { name: "", category: "Traiteur", contact: "", phone: "", email: "", ...(isCollaboratorPortal ? {} : { price: 0, paid: 0 }), status: "to_contact", notes: "" };
  const [form, setForm] = useState(emptyVendor);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const load = () => api.get("/vendors").then((r) => setVendors(r.data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    await api.post("/vendors", form);
    setForm(emptyVendor);
    load();
    toast.success("Prestataire ajouté");
  };

  const update = async (id, changes) => {
    await api.put(`/vendors/${id}`, changes);
    load();
  };

  const startEdit = (v) => {
    setEditingId(v.id);
    setEditForm({ ...v });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = async () => {
    if (!editForm.name.trim()) return;
    await update(editingId, editForm);
    setEditingId(null);
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ?")) return;
    await api.delete(`/vendors/${id}`);
    load();
  };

  const totalBudget = vendors.reduce((s, v) => s + (v.price || 0), 0);
  const totalPaid = vendors.reduce((s, v) => s + (v.paid || 0), 0);

  return (
    <div className="vendors-page mx-auto max-w-7xl">
      <div className="vendors-hero mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-3 text-charcoal/50">
            <CalendarHeart className="w-3.5 h-3.5" />
            <span className="uppercase tracking-[0.2em] font-semibold text-[11px]">Organisation & Logistique</span>
          </div>
          <h1 className="font-serif text-4xl mt-2 text-charcoal font-light">Prestataires</h1>
        </div>
        {!isCollaboratorPortal ? (
          <div className="flex gap-6">
            <div>
              <div className="overline text-charcoal-soft">Budget</div>
              <div className="font-serif text-3xl text-charcoal">{totalBudget.toLocaleString()}€</div>
            </div>
            <div>
              <div className="overline text-charcoal-soft">Payé</div>
              <div className="font-serif text-3xl text-sage">{totalPaid.toLocaleString()}€</div>
            </div>
          </div>
        ) : null}
      </div>

      <form onSubmit={add} className="vendors-form mb-8 grid grid-cols-1 gap-3 md:grid-cols-6" data-testid="vendor-form">
        <select
          value={form.category}
          onChange={(e) => setForm({ ...form, category: e.target.value })}
          className="px-3 py-2.5 border border-border-cream bg-parchment text-sm text-charcoal focus:border-sage focus:outline-none rounded-sm"
        >
          {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
        </select>
        <input
          required
          placeholder="Nom *"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="md:col-span-2 px-3 py-2.5 border border-border-cream bg-parchment text-sm text-charcoal focus:border-sage focus:outline-none rounded-sm"
          data-testid="vendor-name"
        />
        {!isCollaboratorPortal ? (
          <input
            type="number"
            placeholder="Prix (€)"
            value={form.price || ""}
            onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })}
            className="px-3 py-2.5 border border-border-cream bg-parchment text-sm text-charcoal focus:border-sage focus:outline-none rounded-sm"
          />
        ) : null}
        <input
          placeholder="Contact"
          value={form.contact}
          onChange={(e) => setForm({ ...form, contact: e.target.value })}
          className="px-3 py-2.5 border border-border-cream bg-parchment text-sm text-charcoal focus:border-sage focus:outline-none rounded-sm"
        />
        <button className="btn-sage py-2.5 text-xs font-bold uppercase tracking-wider rounded-sm" data-testid="vendor-submit">Ajouter</button>
      </form>

      <div className="space-y-3">
        {vendors.map((v) => {
          if (editingId === v.id) {
            return (
              <div key={v.id} className="vendors-row vendors-row--editing grid grid-cols-1 gap-3 items-center md:grid-cols-12">
                <div className="md:col-span-3 space-y-2">
                  <select
                    value={editForm.category}
                    onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                    className="w-full px-2 py-1.5 border border-border-cream bg-parchment text-sm text-charcoal focus:border-sage focus:outline-none rounded-sm"
                  >
                    {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                  <input
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="w-full px-2 py-1.5 border border-border-cream bg-parchment text-sm text-charcoal focus:border-sage focus:outline-none rounded-sm"
                  />
                </div>
                <div className="md:col-span-2">
                  <input
                    value={editForm.contact || ""}
                    onChange={(e) => setEditForm({ ...editForm, contact: e.target.value })}
                    className="w-full px-2 py-1.5 border border-border-cream bg-parchment text-sm text-charcoal focus:border-sage focus:outline-none rounded-sm"
                    placeholder="Contact"
                  />
                </div>
                {!isCollaboratorPortal ? (
                  <>
                    <div className="md:col-span-2">
                      <input
                        type="number"
                        value={editForm.price || ""}
                        onChange={(e) => setEditForm({ ...editForm, price: parseFloat(e.target.value) || 0 })}
                        className="w-full px-2 py-1.5 border border-border-cream bg-parchment text-sm text-charcoal focus:border-sage focus:outline-none rounded-sm"
                        placeholder="Prix total"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <input
                        type="number"
                        value={editForm.paid || ""}
                        onChange={(e) => setEditForm({ ...editForm, paid: parseFloat(e.target.value) || 0 })}
                        className="w-full px-2 py-1.5 border border-border-cream bg-parchment text-sm text-charcoal focus:border-sage focus:outline-none rounded-sm"
                        placeholder="Payé"
                      />
                    </div>
                  </>
                ) : null}
                <div className="md:col-span-2">
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                    className="w-full px-2 py-1.5 border border-border-cream bg-parchment text-sm text-charcoal focus:border-sage focus:outline-none rounded-sm"
                  >
                    {STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                </div>
                <div className="md:col-span-1 text-right flex flex-col gap-2 items-end">
                  <button onClick={saveEdit} className="text-xs text-sage hover:text-sage/80 underline font-semibold">OK</button>
                  <button onClick={cancelEdit} className="text-xs text-charcoal-soft hover:text-charcoal underline font-light">Annuler</button>
                </div>
              </div>
            );
          }

          return (
            <div key={v.id} className="vendors-row grid grid-cols-1 gap-3 items-center md:grid-cols-12" data-testid={`vendor-${v.id}`}>
              <div className="md:col-span-3">
                <div className="overline text-sage text-[10px]">{v.category}</div>
                <div className="font-serif text-xl text-charcoal">{v.name}</div>
              </div>
              <div className="md:col-span-2 text-sm text-charcoal-soft font-light">{v.contact || "—"}</div>
              {!isCollaboratorPortal ? (
                <>
                  <div className="md:col-span-2 text-sm text-charcoal font-semibold">{(v.price || 0).toLocaleString()}€</div>
                  <div className="md:col-span-2">
                    <input
                      type="number"
                      value={v.paid || ""}
                      onChange={(e) => update(v.id, { paid: parseFloat(e.target.value) || 0 })}
                      className="w-full px-2 py-1.5 border border-border-cream bg-parchment text-sm text-charcoal focus:border-sage focus:outline-none rounded-sm"
                      placeholder="Payé"
                    />
                  </div>
                </>
              ) : null}
              <div className="md:col-span-2">
                <select
                  value={v.status}
                  onChange={(e) => update(v.id, { status: e.target.value })}
                  className="w-full px-2 py-1.5 border border-border-cream bg-parchment text-sm text-charcoal focus:border-sage focus:outline-none rounded-sm"
                >
                  {STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </div>
              <div className="md:col-span-1 text-right flex flex-col gap-2 items-end">
                <button onClick={() => startEdit(v)} className="text-xs text-sage hover:text-sage/80 underline font-light">Modifier</button>
                <button onClick={() => remove(v.id)} className="text-xs text-danger hover:text-danger/80 underline font-light">Suppr.</button>
              </div>
            </div>
          );
        })}
        {vendors.length === 0 && <div className="vendors-empty py-8 text-center text-sm font-light text-charcoal-soft">Aucun prestataire enregistré</div>}
      </div>
    </div>
  );
}
